import { Component, h, Host, Prop, State } from '@stencil/core';
import { translate, Translate } from '~service/translation-service';

const sendGa = (command: 'event', eventName: Gtag.EventNames | (string & {}), eventParams?: Gtag.ControlParams | Gtag.EventParams | Gtag.CustomParams) => {
  if (typeof gtag === 'function') {
    gtag(command, eventName, eventParams || {});
  }
};

@Component({
  tag: 'staedtler-rating',
})
export class Rating {
  @Prop()
  upvoteUri: string;

  @Prop()
  downvoteUri: string;

  @Prop()
  commentUri: string;

  @Prop()
  currentPage: string;

  @State()
  voted = 0;

  @State()
  feedbackForm = {
    active: false,
    loading: false,
    submitted: false,
  };

  componentDidLoad() {
    const vote = localStorage.getItem(this.currentPage);
    if (vote) {
      this.voted = JSON.parse(vote).vote;
    }
  }

  upvote() {
    this.sendVote(this.upvoteUri, 1);
  }

  downvote() {
    if (this.voted !== 0) {
      return;
    }

    this.sendVote(this.downvoteUri, -1);
    setTimeout(() => {
      this.feedbackForm = Object.assign({}, this.feedbackForm, { active: true });
    }, 350);
  }

  submitFeedback(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const comment = (form.querySelector('[name="comment"]') as HTMLInputElement).value || '';
    this.feedbackForm = Object.assign({}, this.feedbackForm, { loading: true });

    if (comment) {
      sendGa('event', 'feedback_submit');
      return fetch(this.commentUri, {
        method: 'POST',
        body: JSON.stringify({
          currentPage: this.currentPage,
          comment: comment,
        }),
        headers: {
          'content-type': 'application/json',
        },
        credentials: 'same-origin',
      })
        .then(() => this.submitFeedbackSuccess())
        .catch(() => this.submitFeedbackSuccess());
    }
  }

  submitFeedbackSuccess() {
    this.feedbackForm = Object.assign({}, this.feedbackForm, { loading: false, submitted: true });
    setTimeout(() => {
      this.feedbackForm = Object.assign({}, this.feedbackForm, { active: false, loading: false, submitted: false });
    }, 2500);
  }

  sendVote(href: string, vote: number) {
    if (this.voted !== 0) {
      return;
    }
    this.voted = vote;
    const body = JSON.stringify({
      vote: vote,
      currentPage: this.currentPage,
    });
    localStorage.setItem(this.currentPage, body);
    fetch(href, {
      method: 'POST',
      body: body,
      headers: {
        'content-type': 'application/json',
      },
      credentials: 'same-origin',
    })
      .then(() => {
        sendGa('event', 'feedback_click', {
          label: vote === 1 ? 'Thumbs up' : 'Thumbs down',
          vote: vote,
        });
      })
      .catch(() => {
        localStorage.removeItem(this.currentPage);
      });
  }

  render() {
    const form = !this.feedbackForm.active ? (
      ''
    ) : (
      <form class="feedback-form" onSubmit={e => this.submitFeedback(e)}>
        <h4>{Translate.translate('feedback.form.headline')}</h4>
        <div class="form-group">
          <textarea required={true} name="comment" class="form-control" placeholder={Translate.translate('feedback.ive-got-an-error-placeholder')} />
        </div>
        <div class="form-group">
          <button class={'btn btn-primary btn-block' + (this.feedbackForm.loading ? ' btn-loading' : '')} type="submit">
            {Translate.translate('feedback.send-feedback-button')}
          </button>
        </div>
      </form>
    );

    const formSuccess = (
      <div class="feedback-form">
        <h4>{Translate.translate('feedback.form.success')}</h4>
      </div>
    );

    return (
      <Host class={'container'}>
        <div class={'feedback' + (this.feedbackForm.active ? ' show-feedback-form' : '')}>
          <h4>{Translate.translate('feedback.headline')}</h4>

          <div class="feedback-icons">
            <a role="button" aria-label={translate('feedback.like')} class={'feedback-positiv' + (this.voted === 1 ? ' active' : '')} onClick={() => this.upvote()}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="22" height="22">
                <path d="M19.291 11.207c.452-.51.709-1.354.709-2.158 0-.531-.117-.996-.34-1.345A1.495 1.495 0 0 0 18.357 7h-2.67c1.453-2.591 1.82-4.543 1.091-5.807C16.269.312 15.337 0 14.692 0a.506.506 0 0 0-.505.433c-.182 1.319-1.211 2.893-2.823 4.318a15.926 15.926 0 0 1-5.391 3.11A1.534 1.534 0 0 0 4.59 7H1.53C.686 7 0 7.674 0 8.5v9c0 .826.686 1.5 1.53 1.5h3.06c.646 0 1.2-.396 1.424-.953 2 .246 2.752.64 3.482 1.022.912.478 1.773.93 4.592.93.735 0 1.426-.188 1.949-.529.51-.333.836-.8.944-1.343.407-.162.768-.536 1.044-1.092.27-.54.444-1.232.444-1.76 0-.1-.006-.19-.017-.275.258-.186.49-.473.68-.85a4.25 4.25 0 0 0 .44-1.827c0-.424-.08-.777-.238-1.05l-.04-.066h-.003zM4.588 18h-3.06a.505.505 0 0 1-.51-.5v-9c0-.275.228-.5.51-.5h3.06c.281 0 .51.225.51.5v9c0 .276-.229.5-.51.5zm14.115-7.726c-.154.304-.31.414-.378.414a.505.505 0 0 0-.51.5c0 .276.229.5.51.5.043 0 .074 0 .12.078.067.117.106.32.106.558 0 .445-.129.974-.335 1.382-.202.4-.407.544-.497.544a.505.505 0 0 0-.51.5c0 .177.094.333.236.422.032.317-.12 1.165-.51 1.718-.149.21-.305.33-.427.33a.505.505 0 0 0-.51.5c0 .88-.992 1.28-1.913 1.28-2.563 0-3.28-.376-4.112-.812-.777-.407-1.65-.865-3.856-1.134V8.867a16.957 16.957 0 0 0 5.927-3.373c1.625-1.436 2.692-2.988 3.054-4.426.278.087.59.27.792.618.34.59.595 2.096-1.523 5.557a.49.49 0 0 0-.006.504c.09.157.26.254.443.254h3.553c.203 0 .333.07.438.234.12.187.185.477.185.815 0 .424-.104.882-.277 1.224z" />
              </svg>
            </a>
            <a role="button" aria-label={translate('feedback.dislike')} class={'feedback-negativ' + (this.voted === -1 ? ' active' : '')} onClick={() => this.downvote()}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="22" height="22">
                <path d="M19.291 11.207c.452-.51.709-1.354.709-2.158 0-.531-.117-.996-.34-1.345A1.495 1.495 0 0 0 18.357 7h-2.67c1.453-2.591 1.82-4.543 1.091-5.807C16.269.312 15.337 0 14.692 0a.506.506 0 0 0-.505.433c-.182 1.319-1.211 2.893-2.823 4.318a15.926 15.926 0 0 1-5.391 3.11A1.534 1.534 0 0 0 4.59 7H1.53C.686 7 0 7.674 0 8.5v9c0 .826.686 1.5 1.53 1.5h3.06c.646 0 1.2-.396 1.424-.953 2 .246 2.752.64 3.482 1.022.912.478 1.773.93 4.592.93.735 0 1.426-.188 1.949-.529.51-.333.836-.8.944-1.343.407-.162.768-.536 1.044-1.092.27-.54.444-1.232.444-1.76 0-.1-.006-.19-.017-.275.258-.186.49-.473.68-.85a4.25 4.25 0 0 0 .44-1.827c0-.424-.08-.777-.238-1.05l-.04-.066h-.003zM4.588 18h-3.06a.505.505 0 0 1-.51-.5v-9c0-.275.228-.5.51-.5h3.06c.281 0 .51.225.51.5v9c0 .276-.229.5-.51.5zm14.115-7.726c-.154.304-.31.414-.378.414a.505.505 0 0 0-.51.5c0 .276.229.5.51.5.043 0 .074 0 .12.078.067.117.106.32.106.558 0 .445-.129.974-.335 1.382-.202.4-.407.544-.497.544a.505.505 0 0 0-.51.5c0 .177.094.333.236.422.032.317-.12 1.165-.51 1.718-.149.21-.305.33-.427.33a.505.505 0 0 0-.51.5c0 .88-.992 1.28-1.913 1.28-2.563 0-3.28-.376-4.112-.812-.777-.407-1.65-.865-3.856-1.134V8.867a16.957 16.957 0 0 0 5.927-3.373c1.625-1.436 2.692-2.988 3.054-4.426.278.087.59.27.792.618.34.59.595 2.096-1.523 5.557a.49.49 0 0 0-.006.504c.09.157.26.254.443.254h3.553c.203 0 .333.07.438.234.12.187.185.477.185.815 0 .424-.104.882-.277 1.224z" />
              </svg>
            </a>
          </div>
          {this.feedbackForm.submitted ? formSuccess : form}
        </div>
      </Host>
    );
  }
}
